import { HeaderButtonIdentifier } from '@/components/elements/HeaderButton/HeaderButton.types';
import CheckoutViewLayout from '@/components/layouts/CheckoutViewLayout';
import SEO, { seoPropsFromBaseString } from '@/components/modules/SEO';
import { ROUTE_BUILDER } from '@/constants/pages';
import { ServiceDetailsProvider } from '@/features/booking-selection/components/ServiceDetailsContext';
import { bookingFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import { hasDealCampagin } from '@/helpers/placeHelperTS';
import { slug } from '@/helpers/urlTs';
import { useAppSelector } from '@/hooks';
import { ListCardsV2Provider } from '@/hooks/useListCardsV2';
import { BookState } from '@/types/state/book';
import { url } from '@/helpers/url';
import { classnames } from '@/helpers/general';
import { limitString } from '@/helpers/seo';
import { _t } from '@/locale';
import { useEffect, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useBookingFlowChrome } from './BookingFlowChromeContext';
import BookingFlowColumns, { MainColumn, SideColumn } from './BookingFlowColumns';
import { BookingFlowContentInset, BookingFlowPageProps } from './BookingFlowPage.types';
import { BOOKING_FLOW_STEP_BACK_LABEL_KEYS } from './constants';
import { CALENDAR_ORIGIN, BookingFlowStepOrigin, readBookingFlowHistoryState } from './goToStep';
import { emitRewind } from './tracking';
import { parseSelection, serialiseSelection } from './urlState';
import { useBookingFlowStepTracking } from './useBookingFlowStepTracking';

const STEP_TITLE_KEYS = {
  // The calendar reached by /valj-utovare's back button, titled as PickTimePage titles itself.
  [CALENDAR_ORIGIN]: 'pickTime',
  ...BOOKING_FLOW_STEP_BACK_LABEL_KEYS,
} as const;

/**
 * Shared by both branches so the steps line up with each other. The measure, the centering and
 * the desktop gutter stay here whatever `contentInset` a step asks for: the `columnGap` widths
 * are derived from this box, so a step that moved them would shift the side column off the
 * offset every other step shares.
 */
const CONTENT_VIEW_CLASS = 'lg:px-4xl mx-auto w-full max-w-[1140px]';

/** `bleed` re-adds the insets from `lg` up, leaving only narrow edge-to-edge. */
const CONTENT_INSET_CLASS: Record<BookingFlowContentInset, string> = {
  default: 'px-cv-padding pt-cv-margin pb-3xl',
  bleed: 'lg:pt-cv-margin lg:pb-3xl',
};

/**
 * Shared shell for the /boka/:placeSlugId/* booking-flow step pages: a CheckoutHeader
 * (back + title + subtitle + merchant logo) plus a generic desktop side-column slot.
 * Always noindex — these pages are dark scaffolding behind the booking-flow-redesign flag.
 *
 * Owns the header back button. An in-flow entry carries the goToStep stamp
 * ({ fromStep, backSteps } in history.state): the button is labelled with the previous
 * screen's title and rewinds there through history, un-doing this screen's answer. No stamp
 * means this is the screen the user entered the flow at (normal entry, cold deep link, or a
 * guard-redirect landing): the button reads "Avbryt" and exits to the page the visit came
 * from — the deal page when a deal campaign is picked, the place page otherwise — with the
 * flow params stripped, like the modal's close, unless BookingFlowChromeContext overrides
 * the unstamped slot (the widget has no place page to abort to; a stamped entry always
 * wins). The calendar exit's own `fromLanding` stamp is such a stamp: "Avbryt" rewinds one
 * entry into the flow it came from.
 *
 * The mobile bottom bar goes in `bottomBar`, not `children`, so it renders full-bleed
 * below the content box instead of inheriting its inline padding.
 *
 * The R15 step-heading focus lives in pages/boka/$placeSlugId/steps.tsx — this shell
 * remounts on every step transition, so it cannot tell a transition from a first render.
 */
const BookingFlowPage = ({
  title,
  subtitle,
  merchantLogoUrl,
  sideColumn,
  columnGap,
  contentInset = 'default',
  stickyMainColumn,
  bottomBar,
  emitStepShown = false,
  abortPath,
  trackServiceDetails = true,
  children,
}: BookingFlowPageProps) => {
  const location = useLocation();
  const history = useHistory();
  const { placeSlugId } = useParams<{ placeSlugId: string }>();
  const dealCampaign = hasDealCampagin(useAppSelector((state) => (state.book as BookState)?.campaigns) ?? []);
  const place = useAppSelector((state) => (state.book as BookState | undefined)?.place);
  const user = useAppSelector((state) => state.users?.user);

  const chrome = useBookingFlowChrome();

  const stamp = readBookingFlowHistoryState(location.state);
  const fromStep: BookingFlowStepOrigin | undefined =
    stamp.fromStep && stamp.fromStep in STEP_TITLE_KEYS ? stamp.fromStep : undefined;
  // The calendar exit's stamp (buildTargetLocation). Back from there is a rewind into the
  // flow the visitor came from, not an abort to the place page.
  const fromLanding = Boolean(stamp.fromLanding);
  // The chrome override applies only to unstamped entries; `null` hides the slot.
  const chromeSlot = fromStep || fromLanding ? undefined : chrome.leftSlot;
  const backLabel = fromStep ? _t(STEP_TITLE_KEYS[fromStep]) : chromeSlot ? chromeSlot.label : _t('abort');

  // history.go is async and queued calls apply cumulatively, so a double-click (or the
  // header's icon + text label, which share this handler) would over-jump by 2×backSteps.
  // One navigation per mount; the effect resets the latch if the shell ever survives one.
  const navigatingRef = useRef(false);
  useEffect(() => {
    navigatingRef.current = false;
  }, [location]);

  // Published as `--bottom-bar-h` on the document root so a mobile `stickyMainColumn`
  // (BookingFlowColumns) can reserve exactly this much height for itself: this bar's own
  // height isn't fixed (it carries content-dependent rows and safe-area padding), so the
  // pinned column can't just assume a pixel number the way it does for the fixed header.
  const bottomBarRef = useRef<HTMLDivElement>(null);
  // Keyed on presence, not element identity: callers pass the bar as inline JSX, so identity
  // changes every render, and the observer already tracks size changes on its own.
  const hasBottomBar = Boolean(bottomBar);
  useEffect(() => {
    const el = bottomBarRef.current;
    // jsdom (unit tests) has no ResizeObserver; the pinned column just falls back to its 0px default.
    if (!el || typeof ResizeObserver === 'undefined') {
      document.documentElement.style.setProperty('--bottom-bar-h', '0px');
      return;
    }
    const observer = new ResizeObserver(() => {
      document.documentElement.style.setProperty('--bottom-bar-h', `${el.offsetHeight}px`);
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      document.documentElement.style.setProperty('--bottom-bar-h', '0px');
    };
  }, [hasBottomBar]);

  const seoProps = seoPropsFromBaseString('checkout', `${url.getBaseUrl()}${location.pathname.replace(/^\//, '')}`);
  // The visible step heading doubles as the document title (calendar-page shape,
  // limitString 60), so history entries and SR announcements name the step. The title-less
  // loading shell keeps the generic checkout title; description stays the checkout base —
  // these pages are noindexed, and a composed one would cost copy in three locales.
  const documentTitle = title
    ? limitString(`${title}${subtitle ? ` - ${subtitle}` : ''} - Bokadirekt`, 60)
    : seoProps.title;

  // The loading shell renders the back slot before any session exists (the fallback initiate
  // waits for a real step render), and a cancel with no session is a Sentry message.
  const cancelActiveSession = () => {
    if (bookingFlowTrackingSession.isActive()) bookingFlowTrackingSession.cancelSession();
  };

  const onBackClick = () => {
    if (navigatingRef.current) return;
    navigatingRef.current = true;
    if (fromStep) {
      // Back to the calendar is not a step rewind: the control arm has no event for it. On a
      // calendar entry (fromLanding) the calendar's own POP guard reports the rewind; the shell
      // only reports rewinds between step pages, or the go() below would be counted twice.
      if (fromStep !== CALENDAR_ORIGIN && !fromLanding) emitRewind(fromStep);
      history.go(-(stamp.backSteps ?? 1));
      return;
    }
    if (fromLanding) {
      cancelActiveSession();
      history.go(-1);
      return;
    }
    if (chromeSlot) {
      // The provider owns the navigation, the shell the session: the widget's slot leaves the
      // place for Platsval, which the POP listener below already reports as a cancel, so the
      // header must say the same or a re-entry continues a session browser-back would have ended.
      cancelActiveSession();
      chromeSlot.onBack();
      return;
    }
    cancelActiveSession();
    if (abortPath) {
      history.replace({ pathname: abortPath });
      return;
    }
    // A deal visitor entered from the deal page, not the place page. The entry is told by the
    // `dealId` the deal page put in the query, not by the campaign alone: a deal in `book.campaigns`
    // only says the picked service is deal-eligible, which a place-page entry may be too. No search
    // on the target: `dealId` is a pass-through the flow carries, and it means nothing there.
    const enteredFromDeal = dealCampaign?.id && String(dealCampaign.id) === parseSelection(location.search).dealId;
    if (enteredFromDeal && dealCampaign?.name) {
      history.replace({ pathname: ROUTE_BUILDER.dealDetails(`${slug(dealCampaign.name)}-${dealCampaign.id}`) });
      return;
    }
    // Replace, not push: aborting removes the flow from history, like the modal's close —
    // back from the place page must not return into an abandoned flow.
    history.replace({
      pathname: ROUTE_BUILDER.placePage(placeSlugId),
      search: serialiseSelection(location.search, { ss: null, sr: null, rs: null, et: null }),
    });
  };

  useBookingFlowStepTracking({ emitStepShown, place, user, navigatingRef });

  return (
    <ListCardsV2Provider>
      <ServiceDetailsProvider trackServiceDetails={trackServiceDetails}>
        <CheckoutViewLayout
          header={{
            // `null` from the chrome context (the widget's entry screen) renders no slot.
            leftSlot:
              chromeSlot === null
                ? undefined
                : {
                    button: {
                      item: { icon: 'back', identifier: HeaderButtonIdentifier.Back },
                      'aria-label': backLabel,
                      onClick: onBackClick,
                    },
                    text: backLabel,
                  },
            middleSlot: { title, subtitle },
            rightSlot: merchantLogoUrl ? { imgUrl: merchantLogoUrl } : undefined,
          }}
          // the gradient is desktop-only; mobile takes the flat surface
          wrapperClass="bg-surface_mp lg:bg-gradient-surface-mp min-h-viewport flex flex-col">
          {/* flex-1 absorbs the slack on short steps, keeping bottomBar at the viewport bottom */}
          <div className={classnames(CONTENT_VIEW_CLASS, CONTENT_INSET_CLASS[contentInset], 'flex-1')}>
            {/* A pinned main column is what bounds its content's height, side column or not. */}
            {sideColumn || stickyMainColumn ? (
              <BookingFlowColumns columnGap={columnGap}>
                <MainColumn sticky={stickyMainColumn}>{children}</MainColumn>
                {sideColumn ? <SideColumn>{sideColumn}</SideColumn> : null}
              </BookingFlowColumns>
            ) : (
              children
            )}
          </div>
          {/* sticky, not fixed: iOS displaces fixed bars when it shifts the visual viewport */}
          {bottomBar ? (
            <div ref={bottomBarRef} className="sticky bottom-0 lg:hidden">
              {bottomBar}
            </div>
          ) : null}
          <SEO {...seoProps} title={documentTitle} noindex image={url.getBaseImageUrl()} />
        </CheckoutViewLayout>
      </ServiceDetailsProvider>
    </ListCardsV2Provider>
  );
};

export default BookingFlowPage;
